import { useState } from "react";
import { type Service } from "@/lib/types";
import { Link } from "wouter";
import { useTranslation } from "react-i18next";
import { Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useServiceRatings, getRating } from "@/hooks/use-service-ratings";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Clock, Sparkles, Check, Car, Truck, Ship, Bike, X, Loader2 } from "lucide-react";
import { motion } from "framer-motion";
import { useQuery } from "@tanstack/react-query";
import { API_URL } from "@/config/api";

type VehicleFilter = 'all' | 'sedan' | 'suv' | 'cuv' | 'motorcycle' | 'van' | 'caravan' | 'boat';

interface ServiceCardProps {
  service: Service;
  index: number;
  vehicleFilter?: VehicleFilter;
}

export function ServiceCard({ service, index, vehicleFilter = 'all' }: ServiceCardProps) {
  const { t, i18n } = useTranslation();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [vehicleDialogOpen, setVehicleDialogOpen] = useState(false);
  const [reviewsOpen, setReviewsOpen] = useState(false);
  const isRtl = i18n.language === 'ar';

  // Use language-specific fields from the API
  const serviceName = isRtl ? service.nameAr : service.nameEn;
  const serviceDescription = isRtl ? service.descriptionAr : service.descriptionEn;
  const serviceCategory = service.category;

  // Service rating
  const { data: allRatings } = useServiceRatings();
  const rating = getRating(allRatings, service.id);

  const descriptionItems = serviceDescription.split(/[،,]/).map(item => item.trim()).filter(item => item.length > 0);
  const shortDescription = descriptionItems.slice(0, 2).join(isRtl ? '، ' : ', ');
  const hasMore = descriptionItems.length > 2;

  // Check if service has multiple vehicle prices
  const hasMultiplePrices = service.vehiclePricing &&
    Object.values(service.vehiclePricing).filter(p => p && p > 0).length > 1;

  // Get the price for the selected vehicle filter
  const getSelectedPrice = () => {
    if (service.isPerUnitPricing) {
      return Number(service.price).toFixed(3);
    }
    if (service.vehiclePricing) {
      // Return price for selected filter, or first available price
      if (vehicleFilter !== 'all' && service.vehiclePricing[vehicleFilter]) {
        return Number(service.vehiclePricing[vehicleFilter]).toFixed(3);
      }
      // Fallback to first available price
      if (service.vehiclePricing.sedan) return Number(service.vehiclePricing.sedan).toFixed(3);
      if (service.vehiclePricing.cuv) return Number(service.vehiclePricing.cuv).toFixed(3);
      if (service.vehiclePricing.suv) return Number(service.vehiclePricing.suv).toFixed(3);
      if (service.vehiclePricing.van) return Number(service.vehiclePricing.van).toFixed(3);
      if (service.vehiclePricing.motorcycle) return Number(service.vehiclePricing.motorcycle).toFixed(3);
      if (service.vehiclePricing.caravan) return Number(service.vehiclePricing.caravan).toFixed(3);
      if (service.vehiclePricing.boat) return Number(service.vehiclePricing.boat).toFixed(3);
    }
    return Number(service.price).toFixed(0);
  };

  const getAllPrices = () => {
    if (service.vehiclePricing) {
      const prices: { value: string; type: VehicleFilter; isHighlighted: boolean }[] = [];
      if (service.vehiclePricing.sedan) prices.push({ value: Number(service.vehiclePricing.sedan).toFixed(3), type: 'sedan', isHighlighted: vehicleFilter === 'sedan' });
      if (service.vehiclePricing.cuv) prices.push({ value: Number(service.vehiclePricing.cuv).toFixed(3), type: 'cuv', isHighlighted: vehicleFilter === 'cuv' });
      if (service.vehiclePricing.suv) prices.push({ value: Number(service.vehiclePricing.suv).toFixed(3), type: 'suv', isHighlighted: vehicleFilter === 'suv' });
      if (service.vehiclePricing.van) prices.push({ value: Number(service.vehiclePricing.van).toFixed(3), type: 'van', isHighlighted: vehicleFilter === 'van' });
      if (service.vehiclePricing.motorcycle) prices.push({ value: Number(service.vehiclePricing.motorcycle).toFixed(3), type: 'motorcycle', isHighlighted: vehicleFilter === 'motorcycle' });
      if (service.vehiclePricing.caravan) prices.push({ value: Number(service.vehiclePricing.caravan).toFixed(3), type: 'caravan', isHighlighted: vehicleFilter === 'caravan' });
      if (service.vehiclePricing.boat) prices.push({ value: Number(service.vehiclePricing.boat).toFixed(3), type: 'boat', isHighlighted: vehicleFilter === 'boat' });
      return prices;
    }
    return null;
  };

  const getPriceDisplay = () => {
    // Per-unit pricing (caravan/boat) - show price per foot
    if (service.isPerUnitPricing) {
      return (
        <>
          {Number(service.price).toFixed(3)} {t("services.currency")}
          <span className="text-lg font-normal text-muted-foreground">/{t("booking.feet")}</span>
        </>
      );
    }
    // Vehicle pricing - show selected vehicle price
    if (service.vehiclePricing) {
      return `${getSelectedPrice()} ${t("services.currency")}`;
    }
    return `${Number(service.price).toFixed(0)} ${t("services.currency")}`;
  };

  return (
    <>
      <motion.div        className="group relative isolate bg-card rounded-3xl overflow-hidden border border-border/50 hover:border-primary/50 transition-all duration-300 shadow-lg shadow-blue-900/5 hover:shadow-xl hover:shadow-blue-900/10 flex flex-col h-full"
      >
        <div className="relative h-48 overflow-hidden rounded-t-3xl">
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent z-10" />
          <img
            src={service.imageUrl}
            alt={serviceName}
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
          />
          <div className="absolute bottom-4 left-4 z-20">
            <span className="bg-primary/90 text-primary-foreground text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wider">
              {serviceCategory}
            </span>
          </div>
        </div>

        <div className="p-6 flex flex-col flex-grow">
          <h3 className="text-xl font-bold font-display mb-2">{serviceName}</h3>
          {rating && (
            <button
              onClick={() => setReviewsOpen(true)}
              className="flex items-center gap-1.5 mb-2 hover:opacity-80 transition-opacity"
              data-testid={`button-view-ratings-${service.id}`}
            >
              <div className="flex">
                {[1, 2, 3, 4, 5].map((s) => (
                  <Star
                    key={s}
                    className={`h-3.5 w-3.5 ${s <= Math.round(rating.average) ? "fill-yellow-400 text-yellow-400" : "text-gray-300"}`}
                  />
                ))}
              </div>
              <span className="text-xs font-medium">{rating.average.toFixed(1)}</span>
              <span className="text-xs text-muted-foreground underline">({rating.count} {rating.count === 1 ? "review" : "reviews"})</span>
            </button>
          )}
          <p className="text-muted-foreground text-sm leading-relaxed mb-2">
            {shortDescription}
            {hasMore && '...'}
          </p>
          {hasMore && (
            <button
              onClick={() => setDialogOpen(true)}
              className="text-primary text-sm font-medium hover:underline mb-4 text-start"
              data-testid={`button-see-more-${service.id}`}
            >
              {t("services.seeMore")}
            </button>
          )}

          <div className="flex items-center gap-2 text-sm text-muted-foreground mb-6">
            <Clock className="w-4 h-4 text-primary" />
            <span>{t("services.durationPrefix")} {service.duration} {t("services.minsEstimated")}</span>
          </div>

          <div className="flex items-center justify-between gap-4 mt-auto pt-6 border-t border-border/50">
            <div className="flex flex-col">
              <span className="text-xs text-muted-foreground uppercase font-bold">{t("services.price")}</span>
              {/* Show single price when vehicle type selected or service has single price */}
              {(!service.vehiclePricing || vehicleFilter !== 'all' || !hasMultiplePrices) ? (
                <div className="flex items-baseline gap-1">
                  <span className="text-2xl font-bold text-primary">{getPriceDisplay()}</span>
                </div>
              ) : null}
              {/* Show list of vehicle types with prices when multiple prices exist and no filter */}
              {hasMultiplePrices && vehicleFilter === 'all' && getAllPrices() && (
                <div className="mt-1 space-y-1">
                  {getAllPrices()!.map((price) => (
                    <div key={price.type} className="flex justify-between items-center text-xs gap-4">
                      <span className="text-muted-foreground">{t(`booking.${price.type}`)}</span>
                      <span className="font-medium">{price.value} {t("services.currency")}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            {hasMultiplePrices ? (
              <Button onClick={() => setVehicleDialogOpen(true)} className="btn-hover rounded-xl px-6">{t("services.bookService")}</Button>
            ) : (
              <Link href={`/booking?serviceId=${service.id}`}>
                <Button className="btn-hover rounded-xl px-6">{t("services.bookService")}</Button>
              </Link>
            )}
          </div>
        </div>
      </motion.div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto p-0 rounded-2xl border-0 shadow-2xl [&>[data-radix-dialog-close]]:hidden">
          <button
            onClick={() => setDialogOpen(false)}
            className="absolute right-4 top-4 z-50 rounded-full bg-black/50 hover:bg-black/70 text-white p-2 backdrop-blur-sm transition-all"
            data-testid="button-close-service-dialog"
          >
            <X className="h-4 w-4" />
          </button>
          <div className="relative h-44 w-full">
            <img
              src={service.imageUrl}
              alt={serviceName}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-background via-background/20 to-transparent" />
          </div>

          <div className="px-6 pb-6 -mt-6 relative z-10">
            <DialogHeader className="mb-3 text-start">
              <DialogTitle className="text-xl font-bold font-display leading-tight text-start">{serviceName}</DialogTitle>
            </DialogHeader>

            <div className="flex flex-wrap items-center gap-3 mb-5 pb-5 border-b border-border/50">
              <div className="flex flex-col">
                {/* Show single price when vehicle type selected or service has single price */}
                {(!service.vehiclePricing || vehicleFilter !== 'all' || !hasMultiplePrices) ? (
                  <div className="flex items-baseline gap-1">
                    <span className="text-2xl font-bold">{getPriceDisplay()}</span>
                  </div>
                ) : null}
                {/* Show list of vehicle types with prices when multiple prices exist and no filter */}
                {hasMultiplePrices && vehicleFilter === 'all' && getAllPrices() && (
                  <div className="mt-1 space-y-1">
                    {getAllPrices()!.map((price) => (
                      <div key={price.type} className="flex justify-between items-center text-xs gap-4">
                        <span className="text-muted-foreground">{t(`booking.${price.type}`)}</span>
                        <span className="font-medium">{price.value} {t("services.currency")}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="flex items-center gap-1.5 text-sm text-muted-foreground ms-auto bg-muted/50 px-3 py-1 rounded-full">
                <Clock className="w-3.5 h-3.5" />
                <span>{t("services.durationPrefix")} {service.duration} {t("services.minsEstimated")}</span>
              </div>
            </div>

            <div className="mb-6">
              <div className="flex items-center gap-2 mb-4">
                <Sparkles className="w-4 h-4 text-primary" />
                <span className="text-sm font-bold uppercase tracking-wide">{t("services.whatsIncluded")}</span>
              </div>

              <ul className="space-y-2.5">
                {descriptionItems.map((item, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <div className="w-5 h-5 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check className="w-3 h-3 text-primary" />
                    </div>
                    <span className="text-sm text-muted-foreground leading-relaxed">{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            <Link href={`/booking?serviceId=${service.id}`}>
              <Button className="w-full rounded-xl" size="lg" data-testid={`button-select-service-${service.id}`}>
                {t("services.selectService")}
              </Button>
            </Link>
          </div>
        </DialogContent>
      </Dialog>

      {/* Vehicle Type Selection Dialog */}
      <Dialog open={vehicleDialogOpen} onOpenChange={setVehicleDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t("booking.selectVehicleType")}</DialogTitle>
            <DialogDescription>{serviceName}</DialogDescription>
          </DialogHeader>
          {service.vehiclePricing && (
            <div className="grid grid-cols-3 gap-4 py-4">
              {Object.entries(service.vehiclePricing)
                .filter(([_, price]) => price !== null && price !== undefined && Number(price) > 0)
                .sort(([a], [b]) => {
                  const order: Record<string, number> = { sedan: 1, cuv: 2, suv: 3, van: 4, motorcycle: 5, caravan: 6, boat: 7 };
                  return (order[a] || 99) - (order[b] || 99);
                })
                .map(([vehicleType, price]) => {
                  const vehicleIcons: Record<string, React.ReactNode> = {
                    sedan: <Car className="h-8 w-8" />,
                    suv: <Car className="h-8 w-8" />,
                    cuv: <Car className="h-8 w-8" />,
                    van: <Truck className="h-8 w-8" />,
                    motorcycle: <Bike className="h-8 w-8" />,
                    caravan: <Truck className="h-8 w-8" />,
                    boat: <Ship className="h-8 w-8" />,
                  };
                  return (
                    <Link
                      key={vehicleType}
                      href={`/booking?serviceId=${service.id}&vehicle=${vehicleType}`}
                      onClick={() => setVehicleDialogOpen(false)}
                    >
                      <Button
                        variant="outline"
                        className="h-24 w-full flex flex-col items-center justify-center gap-2"
                      >
                        {vehicleIcons[vehicleType] || <Car className="h-8 w-8" />}
                        <span className="font-medium text-sm">{t(`booking.${vehicleType}`)}</span>
                        <span className="text-xs text-muted-foreground">
                          {Number(price).toFixed(3)} {t("services.currency")}
                        </span>
                      </Button>
                    </Link>
                  );
                })}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Service Reviews Dialog */}
      <Dialog open={reviewsOpen} onOpenChange={setReviewsOpen}>
        <DialogContent className="w-[calc(100%-2rem)] sm:w-full sm:max-w-lg max-h-[80vh] overflow-y-auto p-6 sm:p-8">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Star className="h-5 w-5 fill-yellow-400 text-yellow-400" />
              {serviceName} — {t("reviews.title")}
            </DialogTitle>
            {rating && (
              <DialogDescription>
                {rating.average.toFixed(1)} / 5 — {rating.count} {rating.count === 1 ? "review" : "reviews"}
              </DialogDescription>
            )}
          </DialogHeader>
          <ServiceReviewsList serviceId={service.id} />
        </DialogContent>
      </Dialog>
    </>
  );
}

/** Fetches and renders reviews for a specific service. */
function ServiceReviewsList({ serviceId }: { serviceId: string }) {
  const { data: reviews, isLoading } = useQuery({
    queryKey: ["/reviews/services", serviceId],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/reviews/services/${serviceId}`);
      if (!res.ok) return { reviews: [], averageRating: 0, totalRatings: 0 };
      return res.json();
    },
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const reviewList = reviews?.reviews || [];

  if (reviewList.length === 0) {
    return (
      <p className="text-center text-sm text-muted-foreground py-8">
        No reviews yet for this service.
      </p>
    );
  }

  return (
    <div className="space-y-4 py-4">
      {reviewList.map((review: any) => (
        <div key={review.id} className="border rounded-lg p-3 space-y-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center">
                <span className="text-xs font-bold text-primary">
                  {(review.customerName || "?").charAt(0).toUpperCase()}
                </span>
              </div>
              <div>
                <p className="text-sm font-medium">{review.customerName || "Anonymous"}</p>
                {review.maskedPhone && (
                  <p className="text-xs text-muted-foreground">{review.maskedPhone}</p>
                )}
              </div>
            </div>
            <div className="flex">
              {[1, 2, 3, 4, 5].map((s) => (
                <Star
                  key={s}
                  className={`h-3.5 w-3.5 ${s <= (review.rating || 0) ? "fill-yellow-400 text-yellow-400" : "text-gray-300"}`}
                />
              ))}
            </div>
          </div>
          {review.comment && (
            <p className="text-sm text-muted-foreground italic">"{review.comment}"</p>
          )}
        </div>
      ))}
    </div>
  );
}
